import React, { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { tryParseInt } from 'fym-common/src/utils/tryParseInt';
import { TypedMemo } from '../../common/types';
import { LoadingView } from '../../common/views/LoadingView';
import { useAccountId, useUserToken, useUserTokenDecoded } from '../../../redux/slices/appInfo/hooks';
import { useGetAccountsQuery } from '../../../redux/api/fymAPI';
import { ErrorView } from '../../common/views/ErrorView/ErrorView';
import { setAccountId } from '../../../redux/slices/appInfo/slice';
import { useQueryParam } from '../../../utils/hooks/useQueryParam';
import { resolveActiveAccountId } from '../../accountSelector/resolveActiveAccountId';
import { readActiveAccountId, writeActiveAccountId } from '../../accountSelector/activeAccountStorage';
import { demoAccountIds } from '../../accountSelector/demoAccounts';
import { SmartLinksContainerRenderer } from './SmartLinksContainerRenderer';
import { useUser } from '../../users/hooks/useUser';

const SmartLinksContainerComponent: React.FC<React.PropsWithChildren> = ({ children }) => {
  // TODO: make it work not be used on web
  // useListenForNavigation(); // It needs to be located in component which has access to router

  const userToken = useUserToken();
  const userTokenDecoded = useUserTokenDecoded();
  const userId = userTokenDecoded?.id ?? null;
  const licenseId = userTokenDecoded?.licenseId;
  const accountId = useAccountId();
  const dispatch = useDispatch();
  const routeAccountIdString = useQueryParam('accountId', undefined, '');
  const routeAccountId = tryParseInt(routeAccountIdString);
  const {
    accounts,
    isAccountsLoading,
    accountsError,
    refetch: accountsRefetch,
  } = useGetAccountsQuery(undefined, {
    skip: !userToken,
    selectFromResult: ({ data, isLoading, error }) => {
      const _accounts = data?.accounts;
      return {
        accounts: _accounts,
        isAccountsLoading: isLoading,
        accountsError: error,
      };
    },
  });
  const { isLoading: isUserLoading, error: userError, refetch: userRefetch } = useUser();

  const refetch = useCallback(() => {
    accountsRefetch().catch(console.log);
    userRefetch().catch(console.log);
  }, [accountsRefetch, userRefetch]);

  useEffect(() => {
    if (!accounts) {
      return;
    }
    const nextAccountId = resolveActiveAccountId({
      accountIds: accounts.map((account) => account.id),
      // design: music24-demo-artist a1. The fallback never picks the demo artist.
      demoAccountIds: demoAccountIds(accounts),
      queryId: routeAccountId ?? null,
      currentId: accountId,
      storedId: userId === null ? null : readActiveAccountId(userId, licenseId),
    });
    if (nextAccountId !== accountId) {
      dispatch(setAccountId({ accountId: nextAccountId }));
    }
  }, [accountId, accounts, dispatch, licenseId, routeAccountId, userId]);

  useEffect(() => {
    if (userId !== null && accountId !== null) {
      writeActiveAccountId(userId, licenseId, accountId);
    }
  }, [accountId, licenseId, userId]);

  if (accountsError) {
    return <ErrorView error={accountsError} retry={refetch} />;
  }
  if (userError) {
    return <ErrorView error={userError} retry={refetch} />;
  }
  if (isAccountsLoading || isUserLoading) {
    return <LoadingView />;
  }

  return <SmartLinksContainerRenderer>{children}</SmartLinksContainerRenderer>;
};

export const SmartLinksContainer = TypedMemo(SmartLinksContainerComponent);
